
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, Trash2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useGroupChat } from "@/hooks/useGroupChat";
import { EventWithAttendees } from "@/services/eventService";
import { format } from "date-fns";

interface EventGroupChatSimpleProps {
  event: EventWithAttendees;
}

const EventGroupChatSimple = ({ event }: EventGroupChatSimpleProps) => {
  const { user } = useAuth();
  const { 
    messages, 
    reactions, 
    typingUsers, 
    loading, 
    sending, 
    connectionStatus,
    sendMessage, 
    handleReaction, 
    deleteMessage, 
    updateTypingStatus 
  } = useGroupChat(event.id);
  
  const [messageInput, setMessageInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const isCreator = event.created_by === user?.id;
  const userAttendance = event.attendees.find(a => a.user_id === user?.id);
  const isUserAdmin = userAttendance?.is_admin || isCreator;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;

    const content = messageInput.trim();
    setMessageInput("");
    await sendMessage(content);
    
    // Stop typing indicator
    if (isTyping) {
      setIsTyping(false);
      updateTypingStatus(false);
    }
  };

  const handleInputChange = (value: string) => {
    setMessageInput(value);
    
    // Handle typing indicator
    if (!isTyping && value.length > 0) {
      setIsTyping(true);
      updateTypingStatus(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        updateTypingStatus(false);
      }
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const canDeleteMessage = (message: any) => {
    return message.sender_id === user?.id || isUserAdmin;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading group chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border">
      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message.id} className="flex gap-3 group">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="text-xs bg-gradient-to-br from-pink-100 to-purple-100">
                  {message.user.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-gray-900">
                    {message.user.username}
                  </span>
                  <span className="text-xs text-gray-500">
                    {format(new Date(message.created_at), "HH:mm")}
                  </span>
                  {canDeleteMessage(message) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-1 opacity-0 group-hover:opacity-100"
                      onClick={() => deleteMessage(message.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="text-gray-800">{message.content}</p>
                  
                  {/* Reactions */}
                  {reactions[message.id] && reactions[message.id].length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {['❤️', '👍', '😂', '😮', '😢', '😡'].map(emoji => {
                        const emojiReactions = reactions[message.id]?.filter(r => r.emoji === emoji) || [];
                        if (emojiReactions.length === 0) return null;
                        
                        const userReacted = emojiReactions.some(r => r.user_id === user?.id);
                        
                        return (
                          <Button
                            key={emoji}
                            variant={userReacted ? "default" : "outline"}
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => handleReaction(message.id, emoji)}
                          >
                            {emoji} {emojiReactions.length}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Quick reaction buttons */}
                  <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {['❤️', '👍', '😂'].map(emoji => (
                      <Button
                        key={emoji}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-xs hover:bg-gray-100"
                        onClick={() => handleReaction(message.id, emoji)}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Typing indicators */}
          {typingUsers.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500 italic">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span>
                {typingUsers.map(u => u.username).join(', ')} 
                {typingUsers.length === 1 ? ' is' : ' are'} typing...
              </span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={messageInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={sending}
            className="flex-1"
          />
          <Button 
            onClick={handleSendMessage} 
            disabled={sending || !messageInput.trim()}
            size="sm"
            className="px-3"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventGroupChatSimple;
